/**
 * Context+ triage, as the inbox list shows it (2026-10-21).
 *
 * The backend puts a `triage` object on every row the viewer's AI
 * capability allows: `{ score, bucket, summary, clock }` where `summary`
 * is the top two reasons joined by "; " and `clock` is the deadline
 * card's computed last day (`{ last_day, weekday, days_left, status,
 * basis }`). Everything here is presentation: the words on the chips
 * and which tone they take. The score itself is never shown — the
 * reasons are what an attorney can argue with.
 */

export const INBOX_BUCKET_NEEDS_ATTORNEY = "needs_attorney";
export const INBOX_BUCKET_ROUTINE = "routine";
export const INBOX_BUCKET_FILE_ONLY = "file_only";

export const INBOX_BUCKET_ORDER = Object.freeze([
  INBOX_BUCKET_NEEDS_ATTORNEY,
  INBOX_BUCKET_ROUTINE,
  INBOX_BUCKET_FILE_ONLY,
]);

export const INBOX_BUCKET_LABELS = Object.freeze({
  [INBOX_BUCKET_NEEDS_ATTORNEY]: "Needs you",
  [INBOX_BUCKET_ROUTINE]: "Routine",
  [INBOX_BUCKET_FILE_ONLY]: "File only",
});

export const INBOX_BUCKET_TONES = Object.freeze({
  [INBOX_BUCKET_NEEDS_ATTORNEY]: "needsYou",
  [INBOX_BUCKET_ROUTINE]: "routine",
  [INBOX_BUCKET_FILE_ONLY]: "fileOnly",
});

export const INBOX_BUCKET_TITLES = Object.freeze({
  [INBOX_BUCKET_NEEDS_ATTORNEY]:
    "A decision or proposal with a clock on it, or issues denied or reduced — an attorney must read it",
  [INBOX_BUCKET_ROUTINE]:
    "Exam scheduling, development, fee notices, acknowledgments — the paralegal's pile",
  [INBOX_BUCKET_FILE_ONLY]: "Nothing to do on this one — file it",
});

export const bucketLabel = (bucket) => INBOX_BUCKET_LABELS[bucket] || null;
export const bucketTone = (bucket) => INBOX_BUCKET_TONES[bucket] || "fileOnly";

/**
 * The clock chip for a row, or null when the letter starts no clock.
 *
 * `days_left` is the backend's count from today to the card's computed
 * last day (negative once the window has run). Tone follows how close
 * it is: red inside a week, amber inside a month, quiet after that,
 * brown once closed.
 */
export const clockChipFor = (clock) => {
  if (!clock || clock.days_left == null || clock.days_left === "") return null;
  const days = Number(clock.days_left);
  if (Number.isNaN(days)) return null;
  const when = [clock.weekday, clock.last_day].filter(Boolean).join(" ");
  const basis = (clock.basis || "").trim();
  const title = [when ? `Last day ${when}` : "", basis].filter(Boolean).join(" — ");
  if (days < 0) {
    const ago = Math.abs(days);
    return {
      tone: "clockClosed",
      label: `Window closed ${ago} day${ago === 1 ? "" : "s"} ago`,
      title,
    };
  }
  if (days === 0) return { tone: "clockSoon", label: "Closes today", title };
  const label = `${days} day${days === 1 ? "" : "s"} left`;
  if (days <= 7) return { tone: "clockSoon", label, title };
  if (days <= 30) return { tone: "clockNear", label, title };
  return { tone: "clockLater", label, title };
};

/**
 * The reason line for a row: the backend's summary with any clock
 * reason taken out, because the clock already has its own chip and a
 * row should not say "19 days" twice. With the tiers (2026-10-21) the
 * summary leads with the rule that placed the row — "money leaving —
 * proposed reduction or severance", "Board decision on your matter — 1
 * issue denied; Court deadline …", "FYI — Todd Bray's matter …",
 * "answered — … filed, then wait" — and that line stays.
 */
export const triageReasonLine = (summary) => {
  if (!summary || typeof summary !== "string") return "";
  return summary
    .split(";")
    .map((part) => part.trim())
    .filter((part) => part && !/^(closes\b|window closes\b|window closed\b)/i.test(part))
    .join("; ");
};
